"use client";

import { motion } from "framer-motion";
import { Shield, UserCheck, Lock, Truck, Clock, Heart, Award } from "lucide-react";
import { HERO, CONFIDENCE_STRIP } from "@/lib/marketing/copy";
import { heroImages } from "@/lib/marketing/images";
import { CTAButton } from "@/components/common/CTAButton";

const iconMap = {
  Shield,
  UserCheck,
  Lock,
  Truck,
};

export function ZaklynHero() {
  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src={heroImages[0].src}
          alt={heroImages[0].alt}
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 zaklyn-container py-20 md:py-32">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight text-foreground leading-[1.1]">
              {HERO.headline.split(".")[0]}.
              <br />
              <span className="zaklyn-gradient-text">
                {HERO.headline.split(".")[1]?.trim() || "Tailored to You."}
              </span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="mt-6 text-lg md:text-xl text-muted-foreground font-body max-w-xl leading-relaxed"
          >
            {HERO.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <CTAButton href="/customer/measurements/book" showArrow>
              {HERO.ctaPrimary}
            </CTAButton>
            <CTAButton href="/customer/orders/new" variant="secondary">
              {HERO.ctaSecondary}
            </CTAButton>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6, ease: "easeOut" }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            {HERO.trustBadges.map((badge, index) => (
              <div key={badge} className="flex items-center gap-2 text-sm text-muted-foreground">
                {index === 0 && <Clock className="h-4 w-4 text-primary" />}
                {index === 1 && <Heart className="h-4 w-4 text-primary" />}
                {index === 2 && <Award className="h-4 w-4 text-primary" />}
                <span className="font-medium">{badge}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8, ease: "easeOut" }}
        className="absolute bottom-0 left-0 right-0 z-10"
      >
        <div className="bg-white/90 backdrop-blur-md border-t border-border/30">
          <div className="zaklyn-container py-4 md:py-5">
            <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-8">
              {CONFIDENCE_STRIP.map((item) => {
                const Icon = iconMap[item.icon as keyof typeof iconMap];
                return (
                  <div
                    key={item.label}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Icon className="h-4 w-4 text-primary" />
                    <span className="font-medium">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
